import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

function AdminPanel({ user }) {
  const [links, setLinks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      const response = await axios.get('/api/links', { withCredentials: true });
      setLinks(response.data);
    } catch (error) {
      console.error('Failed to fetch links:', error);
      setMessage({ type: 'error', text: 'Failed to load links' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    
    try {
      await axios.put('/api/links', links, { withCredentials: true });
      setMessage({ type: 'success', text: 'Links updated successfully!' });
    } catch (error) {
      console.error('Failed to save links:', error);
      setMessage({ type: 'error', text: 'Failed to save changes' });
    } finally {
      setSaving(false);
    }
  };

  const addLink = (sectionId) => {
    const newLink = {
      id: `link-${Date.now()}`,
      name: 'New Link',
      url: 'https://',
      icon: '🔗',
      description: '',
      placeholder: false
    };

    setLinks({
      ...links,
      sections: links.sections.map(section => 
        section.id === sectionId 
          ? { ...section, links: [...section.links, newLink] }
          : section
      )
    });
  };

  const updateLink = (sectionId, linkId, field, value) => {
    setLinks({
      ...links,
      sections: links.sections.map(section => 
        section.id === sectionId 
          ? {
              ...section,
              links: section.links.map(link =>
                link.id === linkId ? { ...link, [field]: value } : link
              )
            }
          : section
      )
    });
  };

  const deleteLink = (sectionId, linkId) => {
    setLinks({
      ...links,
      sections: links.sections.map(section => 
        section.id === sectionId 
          ? {
              ...section,
              links: section.links.filter(link => link.id !== linkId)
            }
          : section
      )
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-sage-400 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <Link to="/" className="text-forest-800 hover:text-forest-700">
                ← Back to Hub
              </Link>
              <h1 className="text-2xl font-bold text-forest-800">
                Manage Links
              </h1>
            </div>
            
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </header>

      {/* Messages */}
      {message && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className={`px-4 py-3 rounded-lg ${
            message.type === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-700'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}>
            {message.text}
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {links && links.sections && links.sections.map((section) => (
          <div key={section.id} className="mb-8 card">
            <h2 className="text-lg font-semibold text-forest-800 mb-4">
              {section.title}
            </h2>
            
            <div className="space-y-3">
              {section.links.map((link) => (
                <div key={link.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Name
                      </label>
                      <input
                        type="text"
                        value={link.name}
                        onChange={(e) => updateLink(section.id, link.id, 'name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sage-400"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        URL
                      </label>
                      <input
                        type="text"
                        value={link.url}
                        onChange={(e) => updateLink(section.id, link.id, 'url', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sage-400"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Icon (Emoji)
                      </label>
                      <input
                        type="text"
                        value={link.icon}
                        onChange={(e) => updateLink(section.id, link.id, 'icon', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sage-400"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <input
                        type="text"
                        value={link.description || ''}
                        onChange={(e) => updateLink(section.id, link.id, 'description', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sage-400"
                        placeholder="Optional description"
                      />
                    </div>
                  </div>
                  
                  <div className="mt-3 flex items-center justify-between">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={link.placeholder || false}
                        onChange={(e) => updateLink(section.id, link.id, 'placeholder', e.target.checked)}
                        className="rounded text-sage-600 focus:ring-sage-400"
                      />
                      <span className="text-sm text-gray-600">Placeholder (Coming Soon)</span>
                    </label>
                    
                    <button
                      onClick={() => deleteLink(section.id, link.id)}
                      className="text-red-600 hover:text-red-700 text-sm font-medium"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
              
              <button
                onClick={() => addLink(section.id)}
                className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-sage-400 hover:text-sage-600 transition-colors"
              >
                + Add Link
              </button>
            </div>
          </div>
        ))}
      </main>
    </div>
  );
}

export default AdminPanel;